import * as React from 'react';
import { Button as FluentButton, type ButtonProps as FluentButtonProps } from '@fluentui/react-components';

type LegacyVariant = 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
type LegacySize = 'default' | 'sm' | 'lg' | 'icon';

export type ButtonProps = Omit<FluentButtonProps, 'appearance' | 'size'> & {
  variant?: LegacyVariant;
  size?: LegacySize;
  asChild?: boolean;
};

function mapAppearance(variant?: LegacyVariant): FluentButtonProps['appearance'] {
  switch (variant) {
    case 'outline':
      return 'outline';
    case 'ghost':
    case 'secondary':
      return 'subtle';
    case 'link':
      return 'transparent';
    case 'destructive':
      // No direct danger appearance in Fluent. Use primary for now; color will come from tokens in Phase 4.
      return 'primary';
    case 'default':
    default:
      return 'primary';
  }
}

function mapSize(size?: LegacySize): FluentButtonProps['size'] {
  switch (size) {
    case 'sm':
      return 'small';
    case 'lg':
      return 'large';
    case 'icon':
    case 'default':
    default:
      return 'medium';
  }
}

export const Button = React.forwardRef<HTMLElement, ButtonProps>((
  { variant = 'default', size = 'default', className, children, ...rest },
  ref
) => {
  return (
    <FluentButton
      appearance={mapAppearance(variant)}
      size={mapSize(size)}
      className={className}
      ref={ref as React.ForwardedRef<HTMLButtonElement | HTMLAnchorElement>}
      {...(rest as FluentButtonProps)}
    >
      {children}
    </FluentButton>
  );
});

Button.displayName = 'Button';


